// Device-authorization ("connect link") flow shared between the server and
// any client that needs a credential it cannot ask the user to paste — the
// terminal client (`reflex-cli`), and apps that connect a Reflex account
// from their own UI. The client starts the flow, shows the user a short
// code + URL, and polls for a token while the user approves it in the
// browser (where they already hold a web session). On approval the server
// mints a personal API key bound to the approving user. The client may replay
// the token poll until it has durably saved the key, then acknowledges the
// handoff so the server can erase its encrypted delivery copy.

import { z } from 'zod';

/**
 * Organization summary rendered on the browser approval page's org picker.
 * Mirrors the shape the MCP OAuth org picker uses so the two stay visually
 * consistent.
 */
export const DeviceOrganizationSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
});
export type DeviceOrganization = z.infer<typeof DeviceOrganizationSchema>;

/** Body a client sends to `POST /api/auth/device/start`. */
export const DeviceStartRequestSchema = z.object({
  /**
   * Human-friendly name of the machine being connected (defaults to its
   * hostname). Surfaced on the browser approval page and used to name the
   * minted API key so the user can recognize/revoke it later.
   */
  hostname: z.string().trim().min(1).max(255).optional(),
  /**
   * Name of the application asking for access, for clients that are not the
   * terminal (an app connecting a Reflex account from its own UI). It is
   * what the approval page asks the user to trust and what names the minted
   * key, so it should be the product's name. Omitted by `reflex-cli`, which
   * identifies itself by machine instead. Self-declared and unverified: it
   * labels the request, it does not authenticate the requester.
   */
  clientName: z.string().trim().min(1).max(80).optional(),
  /**
   * The client will acknowledge after durably storing an approved key. Older
   * clients omit this and retain the original single-poll handoff behavior.
   */
  supportsAcknowledgement: z.boolean().optional(),
});
export type DeviceStartRequest = z.infer<typeof DeviceStartRequestSchema>;

/** Response to `POST /api/auth/device/start`. */
export const DeviceStartResponseSchema = z.object({
  /** Secret the CLI holds and polls with; never shown to the user. */
  deviceCode: z.string(),
  /** Short human code the user confirms in the browser (e.g. `WXYZ-1234`). */
  userCode: z.string(),
  /** Bare approval page URL (no code), shown for manual entry. */
  verificationUri: z.string(),
  /** Approval page URL with the `userCode` prefilled — what the CLI opens. */
  verificationUriComplete: z.string(),
  /** Seconds the CLI should wait between polls. */
  interval: z.number(),
  /** Seconds until the device code expires. */
  expiresIn: z.number(),
  /** Present and true when the server accepted replay-until-ack delivery. */
  acknowledgementRequired: z.boolean().optional(),
});
export type DeviceStartResponse = z.infer<typeof DeviceStartResponseSchema>;

/** Secret device code presented to poll or acknowledge a device sign-in. */
export const DeviceCodeRequestSchema = z.object({
  deviceCode: z.string().min(1),
});
export type DeviceCodeRequest = z.infer<typeof DeviceCodeRequestSchema>;

/** The flow is still waiting on browser approval. Served with HTTP 202. */
export const DeviceTokenPendingSchema = z.object({
  status: z.literal('authorization_pending'),
});

/** Approval succeeded; replayable until the client acknowledges durable storage. */
export const DeviceTokenApprovedSchema = z.object({
  status: z.literal('approved'),
  /** Plaintext personal API key (`rfx_...`) for the approving user. */
  apiKey: z.string(),
  /** Organization the user chose in the browser. */
  organizationId: z.string(),
});

/** Response to `POST /api/auth/device/token`. */
export const DeviceTokenResponseSchema = z.discriminatedUnion('status', [
  DeviceTokenPendingSchema,
  DeviceTokenApprovedSchema,
]);
export type DeviceTokenResponse = z.infer<typeof DeviceTokenResponseSchema>;

/** Response to `GET /api/device/pending?code=<userCode>` (browser session). */
export const DevicePendingResponseSchema = z.object({
  userCode: z.string(),
  /** Name of the machine being connected, when the CLI supplied one. */
  hostname: z.string().nullable(),
  /** Name of the app asking for access, when it is not the terminal. */
  clientName: z.string().nullable(),
  /** The approving user's organizations, for the picker. */
  organizations: z.array(DeviceOrganizationSchema),
});
export type DevicePendingResponse = z.infer<typeof DevicePendingResponseSchema>;

/** Body for `POST /api/device/approve` (browser session). */
export const DeviceApproveRequestSchema = z.object({
  code: z.string().min(1),
  organizationId: z.string().min(1),
});
export type DeviceApproveRequest = z.infer<typeof DeviceApproveRequestSchema>;

/** Body for `POST /api/device/deny` (browser session). */
export const DeviceDenyRequestSchema = z.object({
  code: z.string().min(1),
});
export type DeviceDenyRequest = z.infer<typeof DeviceDenyRequestSchema>;
